/**
 * REEMPLAZA: frontend/src/pages/Dashboard.js
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../api";
import { Navbar, Btn, StatusDot, C } from "../components/UI";

const TEMPLATES = [
  { id:"flask",       icon:"🐍", label:"Python / Flask",     desc:"API REST o web app con Flask" },
  { id:"fastapi",     icon:"⚡", label:"Python / FastAPI",   desc:"API moderna con docs automáticas" },
  { id:"node",        icon:"🟩", label:"Node.js / Express",  desc:"Backend JavaScript con Express" },
  { id:"react",       icon:"⚛️", label:"React App",           desc:"Frontend con React 18" },
  { id:"nextjs",      icon:"🔺", label:"Next.js",            desc:"Full-stack con Next.js 14" },
  { id:"static",      icon:"🌐", label:"HTML / CSS / JS",    desc:"Sitio estático clásico" },
  { id:"telegram",    icon:"✈️", label:"Bot de Telegram",    desc:"Bot con python-telegram-bot" },
  { id:"scraper",     icon:"🕷️", label:"Scraper",            desc:"Automatización con BeautifulSoup" },
  { id:"reactnative", icon:"📱", label:"React Native / Expo",desc:"App mobile (vista web incluida)" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name:"", description:"", template:"flask" });
  const [creating, setCreating] = useState(false);

  useEffect(() => { apiFetch("/projects").then(setProjects).catch(() => {}); }, []);

  const create = async (e) => {
    e.preventDefault(); setCreating(true);
    try {
      const p = await apiFetch("/projects", { method:"POST", body:JSON.stringify(form) });
      nav(`/project/${p.id}`);
    } catch(e) { alert(e.message); } finally { setCreating(false); }
  };

  const deleteProject = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar este proyecto?")) return;
    await apiFetch(`/projects/${id}`, { method:"DELETE" });
    setProjects(p => p.filter(x => x.id !== id));
  };

  const selectedTemplate = TEMPLATES.find(t => t.id === form.template);

  return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", flexDirection:"column" }}>
      <Navbar />
      <div style={{ maxWidth:"960px", margin:"0 auto", padding:"2rem 1.5rem", width:"100%" }}>

        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", marginBottom:"2rem" }}>
          <div>
            <div style={{ fontWeight:700, fontSize:"1.2rem" }}>
              Hola, {user?.username} 👋
            </div>
            <div style={{ color:C.muted, fontSize:"0.82rem", marginTop:"2px" }}>
              {projects.length} proyecto{projects.length !== 1 ? "s" : ""}
            </div>
          </div>
          <Btn style={{ marginLeft:"auto" }} onClick={() => setShowNew(v=>!v)}>
            + Nuevo proyecto
          </Btn>
        </div>

        {/* New project form */}
        {showNew && (
          <div style={{ background:C.surface, border:`1px solid ${C.border}`,
            borderRadius:"14px", padding:"1.75rem", marginBottom:"1.5rem" }}>
            <div style={{ fontWeight:700, fontSize:"1rem", marginBottom:"1.25rem" }}>Nuevo proyecto</div>
            <form onSubmit={create}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"1rem", marginBottom:"1.25rem" }}>
                <div style={{ display:"flex", flexDirection:"column", gap:"5px" }}>
                  <label style={{ fontSize:"0.72rem", color:C.muted, textTransform:"uppercase", letterSpacing:"0.08em" }}>Nombre</label>
                  <input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} required
                    placeholder="Mi proyecto"
                    style={{ background:"#1a1a22", border:`1px solid ${C.border}`, color:C.text,
                      borderRadius:"7px", padding:"9px 13px", fontSize:"0.88rem" }} />
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:"5px" }}>
                  <label style={{ fontSize:"0.72rem", color:C.muted, textTransform:"uppercase", letterSpacing:"0.08em" }}>Descripción</label>
                  <input value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))}
                    placeholder="Opcional"
                    style={{ background:"#1a1a22", border:`1px solid ${C.border}`, color:C.text,
                      borderRadius:"7px", padding:"9px 13px", fontSize:"0.88rem" }} />
                </div>
              </div>

              {/* Template grid */}
              <div style={{ marginBottom:"1.25rem" }}>
                <div style={{ fontSize:"0.72rem", color:C.muted, textTransform:"uppercase",
                  letterSpacing:"0.08em", marginBottom:"10px" }}>Template</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(170px,1fr))", gap:"8px" }}>
                  {TEMPLATES.map(t => (
                    <button key={t.id} type="button" onClick={() => setForm(p=>({...p,template:t.id}))}
                      style={{ padding:"12px", borderRadius:"10px", cursor:"pointer", textAlign:"left",
                        border:`2px solid ${form.template===t.id ? C.accent : C.border}`,
                        background: form.template===t.id ? "#1e1c00" : "transparent",
                        transition:"all 0.15s" }}>
                      <div style={{ fontSize:"1.4rem", marginBottom:"5px" }}>{t.icon}</div>
                      <div style={{ fontSize:"0.78rem", fontWeight:600,
                        color: form.template===t.id ? C.accent : C.text }}>{t.label}</div>
                      <div style={{ fontSize:"0.7rem", color:C.muted, marginTop:"2px", lineHeight:1.4 }}>{t.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display:"flex", gap:"10px", alignItems:"center" }}>
                <Btn type="submit" loading={creating}>
                  Crear con {selectedTemplate?.icon} {selectedTemplate?.label}
                </Btn>
                <Btn variant="ghost" type="button" onClick={() => setShowNew(false)}>Cancelar</Btn>
              </div>
            </form>
          </div>
        )}

        {/* Projects list */}
        {projects.length === 0 ? (
          <div style={{ padding:"4rem", textAlign:"center", border:`1px dashed ${C.border}`,
            borderRadius:"12px", color:C.muted }}>
            <div style={{ fontSize:"2rem", marginBottom:"0.75rem" }}>🚀</div>
            <div style={{ fontWeight:600, marginBottom:"6px" }}>No tenés proyectos todavía</div>
            <div style={{ fontSize:"0.83rem" }}>Creá tu primer proyecto para empezar</div>
          </div>
        ) : (
          <div style={{ display:"grid", gap:"10px" }}>
            {projects.map(p => {
              const tmpl = TEMPLATES.find(t => t.id === p.template);
              return (
                <div key={p.id} onClick={() => nav(`/project/${p.id}`)}
                  style={{ padding:"1.25rem 1.5rem", background:C.surface,
                    border:`1px solid ${C.border}`, borderRadius:"12px", cursor:"pointer",
                    display:"flex", alignItems:"center", gap:"1rem",
                    transition:"border-color 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "#333"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
                  <div style={{ fontSize:"1.6rem", flexShrink:0 }}>{tmpl?.icon || "📁"}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:600, display:"flex", alignItems:"center", gap:"8px" }}>
                      <StatusDot status={p.container_status} />
                      {p.name}
                    </div>
                    <div style={{ color:C.muted, fontSize:"0.78rem", marginTop:"2px" }}>
                      {tmpl?.label} · {p.description || "Sin descripción"} · {new Date(p.last_active).toLocaleDateString("es-AR")}
                    </div>
                  </div>
                  <span style={{ fontSize:"0.72rem", color:p.container_status==="running"?C.success:C.muted,
                    background:"#1a1a22", border:`1px solid ${C.border}`, borderRadius:"4px",
                    padding:"2px 8px", flexShrink:0 }}>{p.container_status}</span>
                  <button onClick={(e) => deleteProject(p.id, e)}
                    style={{ background:"none", border:"none", color:"#444", fontSize:"1.2rem",
                      padding:"4px 8px", cursor:"pointer", flexShrink:0 }}>×</button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
